import merge from 'lodash/merge';

const easingFunctions = {
  easeOutQuad: (time) => time * (2 - time),
  easeInOutQuad: (time) => (time < 0.5 ? 2 * time * time : -1 + (4 - 2 * time) * time),
};

const sleep = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

const isBodyNode = (node) => node === document.body;

const findScrollableParent = (node) => {
  let isBody;
  let hasScrollableSpace;
  let hasVisibleOverflow;
  let targetNode = node;

  do {
    targetNode = targetNode.parentNode;

    // set condition variables
    isBody = isBodyNode(targetNode);

    hasScrollableSpace =
      targetNode.clientHeight < targetNode.scrollHeight ||
      targetNode.clientWidth < targetNode.scrollWidth;

    hasVisibleOverflow = window.getComputedStyle(targetNode, null).overflow === 'visible';
  } while (!isBody && !(hasScrollableSpace && !hasVisibleOverflow));

  isBody = null;
  hasScrollableSpace = null;
  hasVisibleOverflow = null;

  return targetNode;
};

const getDistance = (targetNode, scrollableParent, { left = 0, top = 0 }) => {
  if (!targetNode) {
    // eslint-disable-next-line no-console
    console.warn(`Expected a valid DOM node as the targetNode, but got ${targetNode}.`);
  }

  if (!scrollableParent) {
    // eslint-disable-next-line no-console
    console.warn(`Expected a valid DOM node as the scrollableParent, but got ${scrollableParent}.`);
  }

  const targetNodeRect = targetNode.getBoundingClientRect();
  const parentNodeRect = scrollableParent.getBoundingClientRect();

  const endPosition = {
    x: targetNodeRect.left - parentNodeRect.left - left,
    y: targetNodeRect.top - parentNodeRect.top - top,
  };

  const windowWidth = isBodyNode(scrollableParent) ? window.innerWidth : 0;
  const windowHeight = isBodyNode(scrollableParent) ? window.innerHeight : 0;

  // default to node height if position is beyond max scroll
  const x =
    endPosition.x > parentNodeRect.width - windowWidth
      ? parentNodeRect.width - windowWidth
      : endPosition.x;

  const y =
    endPosition.y > parentNodeRect.height - windowHeight
      ? parentNodeRect.height - windowHeight
      : endPosition.y;

  return { x, y };
};

const getCurrentScroll = (scrollableParent) =>
  isBodyNode(scrollableParent)
    ? { x: window.pageXOffset, y: window.pageYOffset }
    : { x: scrollableParent.scrollLeft, y: scrollableParent.scrollTop };

const doScroll = (scrollableParent, xPosition, yPosition, fix) => {
  const parent = scrollableParent;
  if (isBodyNode(scrollableParent)) {
    window.scrollTo(xPosition, yPosition);
  } else {
    if (!fix.x) parent.scrollLeft = xPosition;
    if (!fix.y) parent.scrollTop = yPosition;
  }
};

/*
  scrollTo - animate scrolling of a UI element (window or otherwise)

  Provide a target DOM node and some options, and this function will
  animate the scroll position to the bounds of the DOM node.

  scrollTo(targetNode, options);
*/
export default function scrollTo(targetNode, config = {}) {
  const defaults = {
    fixAxis: { x: false, y: false },
    padding: { left: 0, top: 0 },
    duration: 800,
    delay: 0,
    easing: 'easeOutQuad',
    onScroll: () => {},
  };

  const options = merge({}, defaults, config);

  if (options.delay > 0) {
    sleep(options.delay).then(() => {
      // reset the delay and go again
      options.delay = 0;
      scrollTo(targetNode, options);
    });
  }

  const parent = findScrollableParent(targetNode);
  const startPosition = getCurrentScroll(parent);
  const distance = getDistance(targetNode, parent, options.padding);
  const easingFunction = easingFunctions[options.easing];

  return new Promise((resolve) => {
    const startTime = Date.now();

    const endPosition = {
      x: startPosition.x + distance.x,
      y: distance.y,
    };

    const scroll = () => {
      const { x: currentX, y: currentY } = getCurrentScroll(parent);

      const increment = (Date.now() - startTime) / options.duration;
      const xStep = distance.x * easingFunction(increment);
      const yStep = (endPosition.y - startPosition.y) * easingFunction(increment);

      options.onScroll();

      if (increment > 1) {
        doScroll(parent, endPosition.x, endPosition.y, options.fixAxis);
        resolve();
      } else {
        doScroll(parent, startPosition.x + xStep, startPosition.y + yStep, options.fixAxis);
        window.requestAnimationFrame(scroll);
      }

      if (currentX === Math.floor(endPosition.x) && currentY === Math.floor(endPosition.y)) {
        resolve();
      }
    };

    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(scroll);
    } else {
      doScroll(parent, distance.x, distance.y, options.fixAxis);
    }
  });
}
